/** Criteria-evaluation analysis mutation state for the patient detail modal. */

import { useCallback, useEffect, useRef, useState } from "react";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import type { CriteriaScreeningResponse } from "../data-model/eligibilityDetail";
import { toUserFacingApiErrorMessage } from "../utils/apiErrorMessage";
import { buildCriteriaEvaluationAnalyzeRequest } from "../utils/criteriaEvaluationModal";

/** Operations and state exposed to the Criteria Evaluation modal. */
export interface CriteriaEvaluationAnalysisController {
  isInitiating: boolean;
  error: string | null;
  criteriaDetail: CriteriaScreeningResponse | null;
  initiate: () => Promise<void>;
  clearError: () => void;
}

/**
 * Initiate a criteria-only evaluation for one patient.
 *
 * @param trialId - Trial against which the patient is evaluated
 * @param mrn - Patient medical record number
 * @returns Criteria evaluation mutation state and controls
 */
export function useCriteriaEvaluationAnalysis(
  trialId: string,
  mrn: string,
): CriteriaEvaluationAnalysisController {
  const [isInitiating, setIsInitiating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [criteriaDetail, setCriteriaDetail] =
    useState<CriteriaScreeningResponse | null>(null);
  const requestController = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      requestController.current?.abort();
    },
    [],
  );

  const clearError = useCallback(() => setError(null), []);

  const initiate = useCallback(async (): Promise<void> => {
    if (!trialId || !mrn) {
      const reason = new Error("Missing trial or MRN");
      setError(reason.message);
      throw reason;
    }

    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    setIsInitiating(true);
    setError(null);
    setCriteriaDetail(null);

    try {
      await clinicalTrialsService.analyzeCriteriaEligibility(
        trialId,
        buildCriteriaEvaluationAnalyzeRequest(mrn),
        controller.signal,
      );
    } catch (reason: unknown) {
      if (controller.signal.aborted) return;
      setError(
        toUserFacingApiErrorMessage(
          reason,
          "Could not initiate criteria evaluation",
        ),
      );
      setIsInitiating(false);
      throw reason;
    }

    try {
      const response = await clinicalTrialsService.getCriteriaEligibilityDetail(
        trialId,
        mrn,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      setCriteriaDetail(response);
      setIsInitiating(false);
    } catch (reason: unknown) {
      if (controller.signal.aborted) return;
      setError(
        toUserFacingApiErrorMessage(
          reason,
          "Could not load criteria evaluation",
        ),
      );
      setIsInitiating(false);
      throw reason;
    }
  }, [mrn, trialId]);

  return { isInitiating, error, criteriaDetail, initiate, clearError };
}
